import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            <h1 className="font-heading text-lg font-semibold">Integritetspolicy</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 prose prose-slate">
        <p className="text-muted-foreground">Senast uppdaterad: Mars 2026</p>

        <h2>1. Inledning</h2>
        <p>
          Livon är ett koncept och en digital tjänst som tillhandahålls och drivs av 
          <strong>Expressa AB</strong>, org.nr 556940-4436, med säte i Sverige.
        </p>
        <p>
          Alla köp, betalningar och avtal ingås med Expressa AB.
        </p>
        <p>
          Livon ("vi", "oss", "vår") värnar om din personliga integritet. Denna integritetspolicy 
          förklarar hur vi samlar in, använder och skyddar dina personuppgifter när du använder 
          vår tjänst på livon.se ("Tjänsten").
        </p>

        <h2>2. Personuppgiftsansvarig</h2>
        <p>
          Personuppgiftsansvarig för behandlingen av dina personuppgifter är:<br />
          <strong>Expressa AB</strong><br />
          Org.nr: 556940-4436<br />
          E-post: support@livon.se
        </p>

        <h2>3. Vilka uppgifter samlar vi in?</h2>
        <p>Vi samlar in följande personuppgifter:</p>
        <ul>
          <li><strong>Kontoinformation:</strong> Namn, e-postadress och lösenord (krypterat)</li>
          <li><strong>Betalningsinformation:</strong> Hanteras av Stripe - vi lagrar inte kortuppgifter</li>
          <li><strong>Användningsdata:</strong> Information du sparar i appen (mål, vanor, anteckningar)</li>
          <li><strong>Teknisk data:</strong> IP-adress, enhetstyp, webbläsare (för säkerhet och felsökning)</li>
        </ul>

        <h2>4. Hur använder vi dina uppgifter?</h2>
        <p>Vi använder dina personuppgifter för att:</p>
        <ul>
          <li>Tillhandahålla och förbättra Tjänsten</li>
          <li>Hantera ditt konto och köp</li>
          <li>Skicka viktiga meddelanden om Tjänsten</li>
          <li>Förhindra bedrägerier och säkerställa säkerhet</li>
          <li>Uppfylla rättsliga skyldigheter</li>
        </ul>

        <h2>5. Rättslig grund</h2>
        <p>Vi behandlar dina personuppgifter baserat på:</p>
        <ul>
          <li><strong>Avtal:</strong> För att kunna leverera Tjänsten du beställt</li>
          <li><strong>Berättigat intresse:</strong> För att förbättra och säkra Tjänsten</li>
          <li><strong>Samtycke:</strong> För marknadsföring (du kan alltid avregistrera dig)</li>
          <li><strong>Rättslig förpliktelse:</strong> För bokföring och skatteändamål</li>
        </ul>

        <h2>6. Hur länge sparar vi uppgifterna?</h2>
        <ul>
          <li><strong>Kontoinformation:</strong> Så länge du har ett aktivt konto + 12 månader</li>
          <li><strong>Betalningshistorik:</strong> 7 år (enligt bokföringslagen)</li>
          <li><strong>Användningsdata:</strong> Raderas när du tar bort ditt konto</li>
        </ul>

        <h2>7. Delning av uppgifter</h2>
        <p>Vi delar endast dina uppgifter med:</p>
        <ul>
          <li><strong>Stripe:</strong> För betalningshantering</li>
          <li><strong>E-postleverantör:</strong> För transaktionella e-postmeddelanden</li>
          <li><strong>Molntjänster:</strong> För säker datalagring</li>
        </ul>
        <p>Vi säljer aldrig dina personuppgifter till tredje part.</p>

        <h2>8. Dina rättigheter (GDPR)</h2>
        <p>Du har rätt att:</p>
        <ul>
          <li><strong>Få tillgång:</strong> Begära en kopia av dina personuppgifter</li>
          <li><strong>Rätta:</strong> Korrigera felaktiga uppgifter</li>
          <li><strong>Radera:</strong> Begära att vi tar bort dina uppgifter</li>
          <li><strong>Begränsa:</strong> Begränsa hur vi använder dina uppgifter</li>
          <li><strong>Dataportabilitet:</strong> Få dina uppgifter i ett maskinläsbart format</li>
          <li><strong>Invända:</strong> Invända mot viss behandling</li>
        </ul>
        <p>Kontakta oss på support@livon.se för att utöva dina rättigheter.</p>

        <h2>9. Säkerhet</h2>
        <p>
          Vi använder branschstandardmetoder för att skydda dina uppgifter, inklusive:
        </p>
        <ul>
          <li>Krypterad dataöverföring (HTTPS/TLS)</li>
          <li>Krypterade lösenord (bcrypt)</li>
          <li>Tvåfaktorsautentisering (valfritt)</li>
          <li>Regelbundna säkerhetsuppdateringar</li>
        </ul>

        <h2>10. Cookies</h2>
        <p>
          Vi använder nödvändiga cookies för att Tjänsten ska fungera (inloggning, sessioner). 
          Läs mer i vår Cookie-policy.
        </p>

        <h2>11. Ändringar</h2>
        <p>
          Vi kan uppdatera denna policy. Väsentliga ändringar meddelas via e-post eller i appen.
        </p>

        <h2>12. Kontakt</h2>
        <p>
          Har du frågor om hur vi hanterar dina personuppgifter?<br />
          E-post: support@livon.se
        </p>
        <p>
          Du har också rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY) om du 
          anser att vi behandlar dina uppgifter felaktigt.
        </p>

        <div className="mt-8 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <p className="text-sm text-emerald-800 m-0">
            <strong>Sammanfattning:</strong> Vi samlar bara in nödvändiga uppgifter, delar dem inte 
            med obehöriga, och du kan när som helst radera ditt konto och alla uppgifter.
          </p>
        </div>
      </main>
    </div>
  );
}
